<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Button,
		ButtonGroup,
		Checkbox,
		Container,
		IconButton,
		Modal,
		Popover,
		Select,
		Text,
	} from '@Shelly/shelly-design-system/index';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Helpers
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { capFirstChar, deepCopy } from 'src/helpers/utilities';
	import { getTemplateService } from 'src/helpers/xt1_helpers/template_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { verifySettingUnsupportedFWVersion } from 'src/helpers/firmware_helpers';
	//Components
	import TemplateInput from '../../Components/TemplateInput.svelte';
	import TemplateInputStatus from '../../Components/TemplateInputStatus.svelte';
	import EvChargerAutoBalanceSelectEm from './EvChargerAutoBalanceSelectEM.svelte';
	//Types
	import type { TemplateService, VCInterfaceStatus } from 'src/types/VirtualComponents';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	export let device: Device;
	export let statuses: VCInterfaceStatus[];
	export let showModal = false;

	type AutoBalance = {
		em_ip: string;
		enable: boolean;
		max_current: number;
		exclude_self_current: boolean;
		timeout_handle: 'stop' | 'keep';
		current_buffer: number;
	};

	const modes = ['current_limit', 'auto_balance'];
	const templateService: TemplateService = getTemplateService(device);
	const currentBuffer = {
		min: 0,
		max: 4,
		unit: 'A',
	};

	const timeoutHandles = [
		{ title: $format('label_stop_charging', { default: 'Stop charging' }), value: 'stop' },
		{
			title: $format('label_keep_charging', {
				default: 'Keep charging using the minimum current',
			}),
			value: 'keep',
		},
	];

	let currentLimit = templateService.settings.current_limit;
	let auto_balance: AutoBalance = deepCopy(templateService?.settings?.auto_balance || {});
	let mode = auto_balance.enable === true ? 'auto_balance' : 'current_limit';
	let currentLimitComponent: TemplateInputStatus;
	let popover = false;
	let waitForResponse = false;

	const totalCurrentLimit = {
		value: auto_balance.max_current ?? 0,
		min: 8,
		max: 40,
		unit: 'A',
	};

	const changeMode = (_mode: string) => {
		mode = _mode;
	};

	const saveAutoBalance = async (auto_balance: AutoBalance) => {
		const res: SettingRequest = await gen2Request(device, 'Service.SetConfig', {
			id: 0,
			config: { auto_balance },
		});
		if (res?.isok) {
			showModal = false;
		}
		onSettingsResponse(res, device);
		return res;
	};

	const saveSetting = async () => {
		waitForResponse = true;
		if (mode === 'current_limit') {
			let res: SettingRequest = await currentLimitComponent.save();
			if (res?.isok && templateService.settings?.auto_balance?.enable === true) {
				auto_balance.enable = false;
				await saveAutoBalance({
					...templateService.settings.auto_balance,
					enable: false,
				});
			} else {
				showModal = !res?.isok;
			}
		} else {
			auto_balance.enable = true;
			await saveAutoBalance(auto_balance);
		}
		waitForResponse = false;
	};

	const hasAutoBalanceErrors = (auto_balance: AutoBalance): boolean => {
		return (
			!auto_balance.em_ip ||
			auto_balance.max_current < totalCurrentLimit.min ||
			auto_balance.max_current > totalCurrentLimit.max ||
			(auto_balance.current_buffer != null &&
				(auto_balance.current_buffer < currentBuffer.min ||
					auto_balance.current_buffer > currentBuffer.max))
		);
	};

	$: currentLimitVC = statuses.find((i) => i?.role == 'current_limit');
	$: settingsErrors =
		(mode === 'auto_balance' && hasAutoBalanceErrors(auto_balance)) ||
		(mode === 'current_limit' &&
			currentLimitComponent &&
			currentLimitComponent.valueOutOfRange(currentLimit)?.error);
</script>

{#if currentLimitVC}
	<Modal
		title={$format('set_up_auto_balance', {
			default: 'Set Up Auto Balance',
		})}
		{showModal}
		on:close={() => {
			showModal = false;
		}}
	>
		<Container slot="content" padding="0">
			<div class="d-flex align-items-center mb-s">
				<Text class="mr-xs" styleType="h2"
					>{$format('schedule_mode_selection', {
						default: 'Mode selection',
					})}</Text
				>
				<Popover
					hideCloseBtn
					show={popover}
					placement="top"
					type="hover"
					on:close={() => (popover = false)}
				>
					<IconButton
						aria-label={$format('button_open_info', { default: 'Open info' })}
						on:click={() => {
							popover = true;
						}}
						icon="fa-solid fa-circle-info"
						variant="help"
					/>
					<svelte:fragment slot="customContent">
						<Text styleType="body-small" class="mb-s"
							>{$format('mode_selection_info_title', {
								default: 'Choose how your charger manages current:',
							})}</Text
						>
						<Text styleType="body-small" class="mb-s"
							><ul>
								<li class="mb-xs">
									{@html $format('mode_selection_info_bullet_1', {
										default:
											'<b>Manual</b>: Set a fixed charging current limit for the EV charger.',
									})}
								</li>
								<li class="mb-s">
									{@html $format('mode_selection_info_bullet_2', {
										default:
											'<b>Auto balance</b>: Charging current is adjusted automatically based on your home’s total energy use, keeping all appliances within a safe limit. Requires a compatible <b>Shelly EM device.</b>',
									})}
								</li>
							</ul></Text
						>
					</svelte:fragment>
				</Popover>
			</div>
			<ButtonGroup
				id="buttongroup-lugry14"
				aria-label={$format('mode_selection', { default: 'Mode selection' })}
				orientation="horizontal"
				class="mb-s"
			>
				{#each modes as _mode}
					<Button
						class={classMap({
							active: mode === _mode,
						})}
						size="m"
						on:click={() => changeMode(_mode)}
						>{capFirstChar($format(`mode_${_mode}`, { default: _mode.replace('_', ' ') }))}
					</Button>
				{/each}
			</ButtonGroup>
			<hr class="mt-m mb-m" />
			{#if mode === 'current_limit'}
				<Text styleType="h2" class="mb-s"
					>{$format('max_charging_current', {
						default: 'Max charging current',
					})}</Text
				>
				<Text styleType="body-small" class="mb-s"
					>{$format('max_charging_current_subtitle', {
						default: 'Limit the maximum charging current to',
					})}
					<span class="text-primary">{currentLimitVC?.rawValue}</span>
					{currentLimitVC.unit}
				</Text>
				<TemplateInputStatus
					{device}
					status={currentLimitVC}
					bind:this={currentLimitComponent}
					bind:value={currentLimit}
					minDecimalDigits={1}
				/>
			{:else}
				<Text styleType="h2" class="mb-s"
					>{$format('label_em_device', {
						default: 'EM device',
					})}</Text
				>
				<EvChargerAutoBalanceSelectEm {device} bind:auto_balance />
				{#if auto_balance.em_ip}
					<Text styleType="h2" class="mb-s mt-m"
						>{$format('total_current_limit', {
							default: 'Total current limit',
						})}</Text
					>
					<Text styleType="body-small" class="mb-s"
						>{$format('total_current_limit_subtitle', {
							default: 'Limit the maximum charging current for the EM and EV charger to',
						})}
						<span class="text-primary">{totalCurrentLimit.value}</span>
						{totalCurrentLimit.unit}.
					</Text>
					<TemplateInput
						bind:value={auto_balance.max_current}
						min={totalCurrentLimit.min}
						max={totalCurrentLimit.max}
						unit={totalCurrentLimit.unit}
						minDecimalDigits={1}
					/>
					{#if verifySettingUnsupportedFWVersion(device, devices_settings.getByRealId(device.id), '2.0.0') === undefined}
						<Text styleType="h2" class="mb-s mt-m"
							>{$format('ev_charger_safety_current_buffer', {
								default: 'Safety current buffer',
							})}</Text
						>
						<Text styleType="body-small" class="mb-s"
							>{$format('ev_charger_safety_current_buffer_subtitle', {
								default:
									'Safety margin subtracted from available current - determines actual charging current.',
							})}
						</Text>
						<TemplateInput
							bind:value={auto_balance.current_buffer}
							min={currentBuffer.min}
							max={currentBuffer.max}
							unit={currentBuffer.unit}
							minDecimalDigits={1}
						/>

						<Text styleType="h2" class="mb-s mt-m"
							>{$format('request_timeout_handle', {
								default: 'Request Timeout Handle',
							})}</Text
						>
						<Text styleType="body-small" class="mb-s"
							>{$format('request_timeout_handle_subtitle', {
								default:
									'Choose what happens when the connection with the EM device cannot be established.',
							})}
						</Text>
						<Select
							aria-label={$format('request_timeout_handle', {
								default: 'Request Timeout Handle',
							})}
							class="width-full"
							icon={null}
							value={timeoutHandles.find((i) => i.value == auto_balance.timeout_handle)}
							options={timeoutHandles}
							on:change={(e) => {
								auto_balance.timeout_handle = e.detail['value'];
							}}
							id="select-request-timeout-handle"
						/>
					{/if}

					<Checkbox
						size="m"
						class="mt-s"
						disabled={waitForResponse}
						bind:checked={auto_balance.exclude_self_current}
						id="checkbox-ihn2v0q"
					>
						{$format('ev_charger_included_measured_current', {
							default: 'Include EV charger consumption in EM readings',
						})}
					</Checkbox>
				{/if}
			{/if}
		</Container>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (showModal = false)}
				>{$format('button_cancel', { default: 'Cancel' })}</Button
			>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				disabled={settingsErrors || waitForResponse}
				loading={waitForResponse}
				on:click={saveSetting}>{$format('button_save', { default: 'Save' })}</Button
			>
		</div>
	</Modal>
{/if}

<style>
	ul {
		padding: 0 0 0 12px;
		margin: 0;
	}
	ul li {
		line-height: 1.3;
	}
</style>
